import {
  ConnectionBlockedError,
  ConnectionTransientError,
  SshEnvironmentGateway,
  type PreparedSshEnvironment,
} from "@afar-research/client-runtime/connection";
import type {
  EnvironmentId,
  SshEnvironmentBootstrap,
  SshEnvironmentDesktopBridge,
  SshEnvironmentEnsureResult,
  SshEnvironmentTarget,
} from "@afar-research/contracts";
import { SSH_PASSWORD_PROMPT_CANCELLED } from "@afar-research/contracts";
import { classifySshFailure } from "@afar-research/shared/sshFailure";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import { desktopIpcFailureDetail } from "../lib/desktopIpcFailure";

/**
 * Opening a forward to an Environment on a machine the user reaches with their own SSH.
 *
 * Only the desktop shell can: the forward is a process on the machine this client runs on. In a
 * browser the bridge is absent and the gateway's own default refuses, so the surface never
 * appears able to connect.
 */

/**
 * Turns what `ssh` said into what the connection ladder should do about it.
 *
 * A changed host key and a refused credential are the person's to answer and must never be
 * retried at them; everything else is reachability, which retries. Classifying here rather than
 * at each call site is what keeps "this needs a password" meaning the same thing in a connection
 * as it does in Computer Setup.
 */
export function sshConnectionError(
  cause: unknown,
): ConnectionBlockedError | ConnectionTransientError {
  const detail = desktopIpcFailureDetail(cause);
  const kind = classifySshFailure(detail);
  // Both host-key verdicts block rather than retry. A changed key is the person's to answer in
  // their own terminal; an unknown one was already put to them as a fingerprint to accept, and
  // arriving here means they declined it or there was no window to ask in.
  if (kind === "changed-host-key" || kind === "unknown-host-key") {
    return new ConnectionBlockedError({ reason: "ssh-host-key", detail });
  }
  if (kind === "authentication") {
    return new ConnectionBlockedError({ reason: "ssh-credential", detail });
  }
  return new ConnectionTransientError({ reason: "ssh-forward", detail });
}

/**
 * A grant that could not be redeemed, as the connection ladder should treat it.
 *
 * Only an Environment that answered and said no has refused anything. A forward that closed or a
 * service that restarted between issuing the grant and redeeming it — which a reinstall does on
 * purpose — got no answer at all, and retrying is what reaches it.
 */
export function sshRedeemError(
  alias: string,
  cause: unknown,
): ConnectionBlockedError | ConnectionTransientError {
  const detail = desktopIpcFailureDetail(cause);
  if (detail.includes("did not answer")) {
    return new ConnectionTransientError({ reason: "ssh-forward", detail: `${alias}: ${detail}` });
  }
  return new ConnectionBlockedError({
    reason: "credential",
    detail: `${alias} refused the Environment Access grant it had just issued: ${detail}`,
  });
}

const cancelledError = (detail: string) =>
  // The person closed the password dialog. That is an answer, and a blocked connection is how
  // the ladder stops asking rather than retrying at them.
  new ConnectionBlockedError({ reason: "ssh-credential", detail });

const wasCancelled = (
  result: SshEnvironmentEnsureResult,
): result is Extract<SshEnvironmentEnsureResult, { readonly type: string }> =>
  (result as { readonly type?: unknown }).type === SSH_PASSWORD_PROMPT_CANCELLED;

const ensureForward = (input: {
  readonly ssh: SshEnvironmentDesktopBridge;
  readonly target: SshEnvironmentTarget;
  readonly installService: boolean;
}) =>
  Effect.tryPromise({
    try: () =>
      input.ssh.ensureEnvironment(input.target, {
        issueBootstrapCredential: true,
        ...(input.installService ? { installService: true } : {}),
      }),
    catch: sshConnectionError,
  }).pipe(
    Effect.flatMap(
      (
        result,
      ): Effect.Effect<
        SshEnvironmentBootstrap,
        ConnectionBlockedError | ConnectionTransientError
      > =>
        wasCancelled(result)
          ? Effect.fail(cancelledError(result.message))
          : Effect.succeed(result as SshEnvironmentBootstrap),
    ),
  );

/**
 * One connection attempt, from a destination to a redeemed session.
 *
 * The grant is redeemed even though the forward is already open, because reaching a machine has
 * never been authorization: the session comes from the Environment that answered, and it is that
 * Environment's own identity the caller checks.
 */
const prepareOverForward = Effect.fn("web.connectionPlatform.ssh.prepareOverForward")(
  function* (input: {
    readonly ssh: SshEnvironmentDesktopBridge;
    readonly target: SshEnvironmentTarget;
    readonly installService: boolean;
  }) {
    const bootstrap = yield* ensureForward(input);
    if (bootstrap.bootstrapCredential === null) {
      return yield* new ConnectionBlockedError({
        reason: "credential",
        detail: `${bootstrap.target.alias} did not issue Environment Access over the SSH forward.`,
      });
    }
    const exchanged = yield* Effect.tryPromise({
      try: () =>
        input.ssh.redeemBootstrapCredential(
          bootstrap.httpBaseUrl,
          bootstrap.bootstrapCredential as string,
        ),
      catch: (cause) => sshRedeemError(bootstrap.target.alias, cause),
    });
    return { bootstrap, exchanged };
  },
);

export const makeSshEnvironmentGateway = (ssh: SshEnvironmentDesktopBridge) =>
  SshEnvironmentGateway.of({
    // Adding a machine puts this Desktop's own build on it, when it has one to put; reconnecting
    // reaches whatever is installed there now.
    provision: Effect.fn("web.connectionPlatform.ssh.provision")(function* (target) {
      const { bootstrap, exchanged } = yield* prepareOverForward({
        ssh,
        target,
        installService: true,
      });
      const descriptor = yield* Effect.tryPromise({
        try: () => ssh.fetchDescriptor(bootstrap.httpBaseUrl, exchanged.session),
        catch: sshConnectionError,
      });
      return {
        environmentId: descriptor.environmentId,
        label: descriptor.label,
        target: bootstrap.target,
        httpBaseUrl: bootstrap.httpBaseUrl,
        wsBaseUrl: bootstrap.wsBaseUrl,
        session: exchanged.session,
      } satisfies PreparedSshEnvironment;
    }),

    prepare: Effect.fn("web.connectionPlatform.ssh.prepare")(function* (input) {
      const { bootstrap, exchanged } = yield* prepareOverForward({
        ssh,
        target: input.target,
        installService: false,
      });
      // The alias is the user's to edit. One that now points at another machine must not be
      // connected to under the identity this client saved for the old one.
      if (exchanged.environmentId !== input.expectedEnvironmentId) {
        return yield* new ConnectionBlockedError({
          reason: "configuration",
          detail: `${bootstrap.target.alias} is a different Environment than the one saved here.`,
        });
      }
      return {
        environmentId: input.expectedEnvironmentId,
        label: bootstrap.target.alias,
        target: bootstrap.target,
        httpBaseUrl: bootstrap.httpBaseUrl,
        wsBaseUrl: bootstrap.wsBaseUrl,
        session: exchanged.session,
      } satisfies PreparedSshEnvironment;
    }),

    disconnect: Effect.fn("web.connectionPlatform.ssh.disconnect")(function* (target) {
      yield* Effect.tryPromise({
        try: () => ssh.disconnectEnvironment(target),
        catch: sshConnectionError,
      });
    }),
  });

export const sshEnvironmentGatewayLayer = Layer.effect(
  SshEnvironmentGateway,
  Effect.sync(() => {
    const ssh = window.desktopBridge?.ssh;
    return ssh === undefined
      ? SshEnvironmentGateway.defaultValue()
      : makeSshEnvironmentGateway(ssh);
  }),
);

export type { EnvironmentId };
